import React, { useMemo, useState } from "react"
import { Formik, useFormikContext } from "formik"
import { Box, Typography } from "@mui/material"
import moment from "moment"
import { COLORS } from "../../../../utils"
import DeliveryModal from "./DeliveryModal"

const weekDays = (isoWeek) => {
  if (!isoWeek) return []
  const today = moment().startOf("day")
  const start = moment(isoWeek, "GGGG-[W]WW").startOf("isoWeek")
  return Array.from({ length: 7 }, (_, i) => {
    const d = start.clone().add(i, "days")
    return { date: d.format("YYYY-MM-DD"), dayNum: d.date(), fullLabel: d.format("dddd D MMMM"), past: d.isBefore(today) }
  })
}

const SHORT_DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]

const DateContent = ({ hasError, onInteract }) => {
  const { values, setFieldValue } = useFormikContext()
  const days = useMemo(() => weekDays(values.week), [values.week])

  const getDaySlot = (date) => values.daySlots.find((s) => s.date === date) || null
  const isSelected = (date) => !!getDaySlot(date)

  const handleCardClick = (date) => {
    onInteract()
    if (isSelected(date)) {
      setFieldValue("daySlots", values.daySlots.filter((s) => s.date !== date))
    } else {
      setFieldValue("daySlots", [...values.daySlots, { date, count: 1 }])
    }
  }

  const handleStep = (date, delta, e) => {
    e.stopPropagation()
    onInteract()
    const slot = getDaySlot(date)
    if (!slot) return
    const next = slot.count + delta
    if (next <= 0) {
      setFieldValue("daySlots", values.daySlots.filter((s) => s.date !== date))
    } else {
      setFieldValue("daySlots", values.daySlots.map((s) => (s.date === date ? { ...s, count: next } : s)))
    }
  }

  const totalDeliveries = values.daySlots.reduce((sum, s) => sum + s.count, 0)
  const totalDays = values.daySlots.length

  return (
    <Box>
      <Box sx={sx.dayGrid} role="group" aria-label="Jours de livraison">
        {days.map(({ date, dayNum, fullLabel, past }, i) => {
          const selected = isSelected(date)
          const slot = getDaySlot(date)
          return (
            <Box
              key={date}
              role={past ? undefined : "button"}
              tabIndex={past ? -1 : 0}
              onClick={past ? undefined : () => handleCardClick(date)}
              sx={{ ...sx.dayCard, ...(past && sx.dayCardPast), ...(selected && sx.dayCardSelected), ...(hasError && !selected && !past && sx.dayCardError) }}
              aria-pressed={past ? undefined : selected}
              aria-label={fullLabel}
              aria-disabled={past || undefined}
            >
              <Typography sx={{ ...sx.dayCardName, ...(selected && { color: "primary.main" }) }}>
                {SHORT_DAYS_FR[i]}
              </Typography>
              <Typography sx={{ ...sx.dayCardDate, ...(selected && { color: "primary.main" }) }}>
                {dayNum}
              </Typography>
              {!past && (
                selected ? (
                  <Box sx={sx.stepper} onClick={(e) => e.stopPropagation()}>
                    <Box sx={sx.stepGroup}>
                      <Box component="button" type="button" sx={sx.stepBtnBase} onClick={(e) => handleStep(date, -1, e)} aria-label={`Retirer une livraison le ${fullLabel}`}>−</Box>
                      <Box component="span" sx={sx.stepCount} aria-live="polite">{slot.count}</Box>
                      <Box component="button" type="button" sx={sx.stepBtnBase} onClick={(e) => handleStep(date, 1, e)} aria-label={`Ajouter une livraison le ${fullLabel}`}>+</Box>
                    </Box>
                  </Box>
                ) : (
                  <Box aria-hidden="true" className="dlv-add" sx={{ ...sx.dayCardPlus, ...(hasError && sx.dayCardPlusError) }}>+</Box>
                )
              )}
            </Box>
          )
        })}
      </Box>
      {hasError && (
        <Typography variant="caption" color="error" sx={{ display: "block", textAlign: "center", mt: 2, fontSize: 16 }}>
          Veuillez sélectionner au moins une date de livraison
        </Typography>
      )}
      <Box sx={sx.liveCount} aria-live="polite">
        <Typography color="text.secondary">
          <strong>{totalDeliveries}</strong>{" "}
          {totalDeliveries <= 1 ? "livraison sélectionnée" : "livraisons sélectionnées"}
          {totalDays < totalDeliveries && <>{" "}sur <strong>{totalDays}</strong>{" "}{totalDays === 1 ? "jour" : "jours"}</>}
        </Typography>
      </Box>
    </Box>
  )
}

const DeliveryDateModal = ({ open, onClose, onCancel, week, initialDaySlots = [], onSubmit }) => {
  const [hasError, setHasError] = useState(false)

  return (
    <Formik initialValues={{ week, daySlots: initialDaySlots }} enableReinitialize>
      {({ values }) => {
        const handleNext = () => {
          if (!values.daySlots || values.daySlots.length === 0) { setHasError(true); return }
          onSubmit(values.daySlots)
        }
        return (
          <DeliveryModal
            open={open}
            onClose={onClose}
            title="Ajouter les jours de livraison"
            subtitle="Sélectionnez les jours où les plats seront livrés."
            leftLabel="Retour"
            onLeft={onCancel}
            submitLabel="Suivant"
            onSubmit={handleNext}
            dialogSx={{ "& .MuiDialog-paper": { minWidth: "max-content" } }}
          >
            <DateContent hasError={hasError} onInteract={() => setHasError(false)} />
          </DeliveryModal>
        )
      }}
    </Formik>
  )
}

const sx = {
  dayGrid: { display: "grid", gridTemplateColumns: "repeat(7, 73px)", gap: "8px" },
  dayCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "8px",
    border: "1px solid",
    borderColor: "divider",
    py: "8px",
    px: "4px",
    cursor: "pointer",
    userSelect: "none",
    transition: "border-color 120ms, background-color 120ms",
    outline: "none",
    "&:hover": { boxShadow: "inset 0 0 0 1px #E0E0E0", backgroundColor: "#F7F7F7", "& .dlv-add": { borderColor: "primary.main", color: "primary.main" } },
    "&:focus-visible": { borderColor: "primary.main", boxShadow: "0 0 0 2px rgba(33,150,243,0.25)" },
  },
  dayCardSelected: { borderColor: "primary.main", bgcolor: "#EEF5FE" },
  dayCardPast: { opacity: 0.38, cursor: "not-allowed", pointerEvents: "none" },
  dayCardError: { borderColor: "error.main" },
  dayCardPlusError: { borderColor: "error.main", color: "error.main" },
  dayCardName: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    color: "text.secondary",
    lineHeight: 1,
  },
  dayCardDate: { fontSize: 18, fontWeight: 500, lineHeight: 1, color: "text.primary" },
  dayCardPlus: {
    width: 22,
    height: 22,
    border: "1.5px dashed",
    borderColor: COLORS.GREY_BORDER,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    lineHeight: 1,
    color: COLORS.GREY_BORDER,
    mt: "6px",
    mb: "4px",
    transition: "border-color 120ms, color 120ms",
  },
  stepper: { display: "flex", alignItems: "center", mt: "4px",mb: "4px" },
  stepGroup: {
    display: "flex",
    alignItems: "center",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "10px",
    overflow: "hidden",
    height: 24,
    bgcolor: "white",
  },
  stepBtnBase: {
    width: 22,
    height: 24,
    border: "none",
    bgcolor: "transparent",
    color: "primary.main",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1,
    p: 0,
    transition: "background-color 100ms",
    "&:hover": { bgcolor: "primary.main", color: "white" },
  },
  stepCount: {
    fontSize: 12,
    fontWeight: 600,
    minWidth: "20px",
    textAlign: "center",
    color: "text.secondary",
    lineHeight: 1,
    px: "2px",
  },
  liveCount: { mt: 2, textAlign: "center", py: 1, px: 2 },
}

export default DeliveryDateModal
